/**
 * La invitación a activar los avisos, al entrar a la aplicación instalada.
 *
 * ── Por qué es una pantalla NUESTRA y no el permiso del sistema ─────────────
 *
 * El permiso del navegador **se pide UNA sola vez en la vida**. Si la persona
 * dice que no, queda en «denegado» para siempre y no hay forma de volver a
 * preguntar: ni desde acá, ni con otro botón, ni borrando nada. Solo se
 * revierte entrando a los ajustes del sistema, cosa que nadie hace.
 *
 * Por eso esto es una invitación propia: explica para qué sirve, y recién
 * cuando alguien dice que sí se dispara el diálogo del sistema — que a esa
 * altura ya tiene la respuesta decidida. Disparar el del sistema de una, al
 * entrar, quemaría el único intento contra alguien que todavía no entendió qué
 * le están pidiendo.
 *
 * ── Cuándo aparece ─────────────────────────────────────────────────────────
 *
 * Solo con la aplicación INSTALADA (abierta desde el ícono). En el navegador
 * suelto no, por dos razones: en iPhone ni siquiera es posible —Safari solo
 * entrega avisos a la app de la pantalla de inicio— y en escritorio, quien
 * entra por una pestaña suelta no está en el momento de decidir eso.
 *
 * Si alguien la cierra, se calla tres días. El dueño la quería en cada
 * entrada; tres días es el acuerdo entre eso y que la aplicación no se vuelva
 * insistente con quien ya dijo «ahora no» — y es una constante, no una
 * decisión de diseño enterrada.
 */

import { useEffect, useState } from 'react';
import { activarPush, estadoPush, instalada } from '@/lib/push';
import { Button, Modal, Note } from '../ui';
import { Icon } from './NavIcons';

/** Cuánto se calla después de un «Ahora no». */
const DIAS_DE_SILENCIO = 3;
const CLAVE = 'cb.avisos.pospuesto';

function pospuestoHastaHoy(): boolean {
  try {
    const hasta = Number(localStorage.getItem(CLAVE) ?? 0);
    return Number.isFinite(hasta) && hasta > Date.now();
  } catch {
    // Modo privado o almacenamiento bloqueado: que no se pueda recordar el
    // «ahora no» es peor que no ofrecerlo, así que se ofrece.
    return false;
  }
}

function posponer(): void {
  try {
    localStorage.setItem(CLAVE, String(Date.now() + DIAS_DE_SILENCIO * 86400_000));
  } catch { /* ver arriba */ }
}

export function InvitarAvisos() {
  const [abierto, setAbierto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      if (!instalada() || pospuestoHastaHoy()) return;
      const e = await estadoPush();
      // `sin-permiso` es el único estado que se puede resolver desde acá.
      // «bloqueado» ya no tiene arreglo por software, y ofrecerlo sería
      // prometer algo que el botón no puede cumplir.
      if (vivo && e === 'sin-permiso') setAbierto(true);
    })();
    return () => { vivo = false; };
  }, []);

  if (!abierto) return null;

  async function activar() {
    setOcupado(true);
    setFallo(null);
    try {
      const e = await activarPush();
      if (e === 'activo') setAbierto(false);
      else if (e === 'bloqueado') {
        setFallo('Quedaron bloqueadas. Se vuelven a permitir desde los ajustes del teléfono.');
      } else {
        setFallo('No se pudieron activar. Probá de nuevo en un momento.');
      }
    } catch {
      setFallo('No se pudieron activar. Probá de nuevo en un momento.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Modal
      title="Notificaciones"
      hoja
      /* Se cierra tocando afuera, a diferencia del recortador de fotos: acá no
         hay trabajo que perder, y una invitación que no se puede esquivar sin
         apuntarle a un botón es más insistente de lo que amerita. */
      onClose={() => { posponer(); setAbierto(false); }}
      footer={
        <>
          <Button onClick={() => { posponer(); setAbierto(false); }} disabled={ocupado}>
            Ahora no
          </Button>
          <span className="spacer" />
          <Button variant="primary" onClick={() => void activar()} disabled={ocupado}>
            {ocupado ? 'Activando…' : 'Activar'}
          </Button>
        </>
      }
    >
      <div className="invitar-avisos">
        {/* El ícono va de la familia y no como emoji: `🔔` lo pinta el sistema
            a todo color y en iPhone salía un campanazo dorado en una interfaz
            que no tiene ni un elemento así (captura del dueño, 8-set-2026). Es
            el mismo motivo por el que la navegación dejó de usar glifos.
            Y es el sobre y no el triángulo de `alerta`: esto es un ofrecimiento,
            no una advertencia, y el triángulo se lee como que algo salió mal. */}
        <span className="invitar-avisos__ico" aria-hidden>
          <Icon name="comunicacion" size={20} />
        </span>
        <div>
          <p className="invitar-avisos__lead">
            Te avisamos en el teléfono cuando confirmamos tu pago o cuando hay
            novedades de tu consultorio, aunque no tengás la aplicación abierta.
          </p>
          <p className="small muted invitar-avisos__pie">
            Las podés cambiar cuando querás desde Mi cuenta.
          </p>
          {fallo && <Note tone="warn">{fallo}</Note>}
        </div>
      </div>
    </Modal>
  );
}
